/**
 * News/NewsDetail.jsx
 * ════════════════════════════════════════════════════════════════
 * Детальний перегляд однієї новини (виїжджаюча збоку панель).
 * Винесено окремо від NewsPublic — перевикористовується і на
 * сторінці садочка (список карток), і на головній сторінці
 * порталу (карусель).
 *
 * Props:
 *   scope   {string}   — 'portal' | 'kg'
 *   kgId    {string?}  — обов'язково для scope='kg'
 *   newsId  {string}
 *   onClose {function}
 * ════════════════════════════════════════════════════════════════
 */

import React, { useState, useEffect } from 'react';
import { Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark, faCalendar, faTag, faPlay } from '@fortawesome/free-solid-svg-icons';
import { constr_uri } from '../../../../../services/apiClient';
import PhotoViewer from '../../../../../components/PhotoViewer/PhotoViewer';
import { newsAPI } from './newsAPI';

// ── Стилі — той самий injectStyles що і в NewsPublic, ідемпотентний ───────────

var STYLES_INJECTED = false;
function injectStyles() {
    if (STYLES_INJECTED) return;
    STYLES_INJECTED = true;
    var s = document.createElement('style');
    s.textContent = [
        '.np-body p { margin: 0 0 12px; }',
        '.np-body h3 { font-size: 17px; font-weight: 700; margin: 18px 0 8px; color: #111827; }',
        '.np-body ul, .np-body ol { padding-left: 24px; margin: 10px 0; }',
        '.np-body li { margin-bottom: 6px; }',
        '.np-overlay { position: fixed; inset: 0; z-index: 9999; background: rgba(0,0,0,0); transition: background 0.3s; pointer-events: none; }',
        '.np-overlay.visible { background: rgba(0,0,0,0.6); pointer-events: all; }',
    ].join('\n');
    document.head.appendChild(s);
}

export function formatNewsDate(d) {
    if (!d) return '';
    return new Date(d).toLocaleDateString('uk-UA', { day: '2-digit', month: 'long', year: 'numeric' });
}

var NewsDetail = function({ scope, kgId, newsId, onClose }) {
    var [item, setItem]       = useState(null);
    var [loading, setLoading] = useState(true);
    var [visible, setVisible] = useState(false);
    var [gallery, setGallery] = useState([]);
    var [galleryDocId, setGalleryDocId] = useState(null);
    var [viewer, setViewer]   = useState(null);

    useEffect(function() { injectStyles(); }, []);

    useEffect(function() {
        newsAPI.getPublicById(scope, kgId, newsId)
            .then(setItem)
            .finally(function() { setLoading(false); });
        newsAPI.getPublicGallery(scope, kgId, newsId)
            .then(function(result) {
                setGallery(result.files || []);
                setGalleryDocId(result.docId);
            })
            .catch(function() {});
        document.body.style.overflow = 'hidden';
        requestAnimationFrame(function() { setVisible(true); });
        return function() { document.body.style.overflow = ''; };
    }, [newsId]);

    var handleViewGalleryItem = function(file) {
        var isVideo = file.source === 'youtube';
        setViewer({
            src:   isVideo ? file.youtube_url : constr_uri('docfile?doc_id=' + galleryDocId + '&file_id=' + file._id),
            name:  file.caption || file.name,
            mtype: isVideo ? 'video/youtube' : file.mtype,
        });
    };

    useEffect(function() {
        var handler = function(e) { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', handler);
        return function() { document.removeEventListener('keydown', handler); };
    }, []);

    var coverSrc = item && item.coverDocId && item.coverFileId
        ? constr_uri('docfile?doc_id=' + item.coverDocId + '&file_id=' + item.coverFileId)
        : null;

    return (
        <>
            <div className={'np-overlay' + (visible ? ' visible' : '')} onClick={onClose} />
            <div style={{
                position: 'fixed', top: 0, right: 0, bottom: 0,
                width: 'min(680px, 100vw)',
                background: '#fff', zIndex: 10000,
                overflowY: 'auto',
                transform: visible ? 'translateX(0)' : 'translateX(100%)',
                transition: 'transform 0.35s cubic-bezier(0.16,1,0.3,1)',
                boxShadow: '-8px 0 40px rgba(0,0,0,0.15)',
            }}>
                {/* Закрити */}
                <button
                    onClick={onClose}
                    style={{
                        position: 'sticky', top: 16, left: '100%', marginRight: 16, marginTop: 16,
                        width: 36, height: 36, borderRadius: '50%',
                        background: 'rgba(0,0,0,0.5)', border: 'none', color: '#fff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: 'pointer', zIndex: 10,
                    }}
                >
                    <FontAwesomeIcon icon={faXmark} />
                </button>

                {loading ? (
                    <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
                ) : !item ? (
                    <div style={{ textAlign: 'center', padding: 40, color: '#adb5bd' }}>Новину не знайдено</div>
                ) : (
                    <div style={{ marginTop: -52 }}>
                        {coverSrc && (
                            <img src={coverSrc} alt={item.title} style={{ width: '100%', height: 280, objectFit: 'cover', display: 'block' }} />
                        )}
                        <div style={{ padding: '24px 32px 48px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
                                {item.categoryName && (
                                    <span style={{
                                        background: item.categoryColor || '#0069ff', color: '#fff',
                                        padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700,
                                    }}>
                                        {item.categoryName}
                                    </span>
                                )}
                                <span style={{ fontSize: 12, color: '#9ca3af', display: 'flex', alignItems: 'center', gap: 5 }}>
                                    <FontAwesomeIcon icon={faCalendar} style={{ fontSize: 11 }} />
                                    {formatNewsDate(item.publishAt)}
                                </span>
                            </div>

                            <h1 style={{ fontSize: 26, fontWeight: 800, color: '#111827', lineHeight: 1.3, marginBottom: 16 }}>
                                {item.title}
                            </h1>

                            {item.body ? (
                                <div className="np-body" style={{ fontSize: 15, lineHeight: 1.8, color: '#374151' }}
                                    dangerouslySetInnerHTML={{ __html: item.body }} />
                            ) : item.excerpt ? (
                                <div style={{ fontSize: 15, lineHeight: 1.8, color: '#374151' }}>{item.excerpt}</div>
                            ) : null}

                            {/* Галерея фото/відео */}
                            {gallery.length > 0 && (
                                <div style={{ marginTop: 24 }}>
                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
                                        gap: 8,
                                    }}>
                                        {gallery.map(function(file) {
                                            var isVideo = file.source === 'youtube';
                                            var thumbSrc = isVideo
                                                ? file.thumbnail_url
                                                : constr_uri('docfile?doc_id=' + galleryDocId + '&file_id=' + file._id);
                                            return (
                                                <div
                                                    key={file._id}
                                                    onClick={function() { handleViewGalleryItem(file); }}
                                                    title={file.caption || ''}
                                                    style={{
                                                        position: 'relative', paddingBottom: '100%',
                                                        borderRadius: 8, overflow: 'hidden',
                                                        cursor: 'pointer', background: '#f3f4f6',
                                                    }}
                                                >
                                                    <img
                                                        src={thumbSrc} alt={file.caption || ''}
                                                        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                                                    />
                                                    {isVideo && (
                                                        <div style={{
                                                            position: 'absolute', inset: 0,
                                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                            background: 'rgba(0,0,0,0.25)',
                                                        }}>
                                                            <div style={{
                                                                width: 30, height: 30, borderRadius: '50%',
                                                                background: 'rgba(255,0,0,0.9)', color: '#fff',
                                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                            }}>
                                                                <FontAwesomeIcon icon={faPlay} style={{ fontSize: 11, marginLeft: 2 }} />
                                                            </div>
                                                        </div>
                                                    )}
                                                    {file.caption && (
                                                        <div style={{
                                                            position: 'absolute', bottom: 0, left: 0, right: 0,
                                                            background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
                                                            padding: '14px 6px 5px',
                                                            fontSize: 10, color: '#fff',
                                                            overflow: 'hidden', textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                        }}>
                                                            {file.caption}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {item.tags && item.tags.length > 0 && (
                                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 24, paddingTop: 20, borderTop: '1px solid #f0f0f0' }}>
                                    {item.tags.map(function(t) {
                                        return (
                                            <span key={t} style={{
                                                fontSize: 12, color: '#6b7280', background: '#f3f4f6',
                                                padding: '3px 10px', borderRadius: 12,
                                                display: 'flex', alignItems: 'center', gap: 4,
                                            }}>
                                                <FontAwesomeIcon icon={faTag} style={{ fontSize: 9 }} />
                                                {t}
                                            </span>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {viewer && (
                <PhotoViewer
                    src={viewer.src}
                    name={viewer.name}
                    mtype={viewer.mtype}
                    onClose={function() { setViewer(null); }}
                />
            )}
        </>
    );
};

export default NewsDetail;